// ---------------------------------------------------------------------------
// Data I/O — JSON export/import for data explorer entities.
// ---------------------------------------------------------------------------

import { foldBundleEventBindings } from '@cogo/publisher-core/event-binding-shape'
import { toast } from '@/store/toast-store'
import { useStudioStore } from '@/store/studio-store'
import type { EntityIndex } from '../hooks/use-entity-index'
import type { DataCategory } from '@/store/types'

// ─── Export ─────────────────────────────────────────────────────

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function exportCategory(category: DataCategory, entities: unknown[]) {
  if (entities.length === 0) {
    toast.warning(`No ${category} to export`)
    return
  }
  downloadJson(entities, `cogo-${category}.json`)
  toast.success(`Exported ${entities.length} ${category}`)
}

interface ProjectBundle {
  version: 1
  projectName: string
  exportedAt: string
  data: {
    variables: unknown[]
    datasources: unknown[]
    events: unknown[]
    flows: unknown[]
  }
}

export interface DataImportResult {
  imported: number
  skipped: number
  errors: string[]
  /**
   * How many imported rows REPLACED a declaration the project already had.
   *
   * `addVariable` / `addDatasource` / `addEventBinding` each write
   * `map[key] = value` unconditionally, so an import over an existing project
   * overwrites. The receipt had no word for that: a user who lost their own
   * declarations read `Imported 3 variables` — true, and the wrong thing to
   * say. Counted here, named below, and reported by `reportImport`.
   */
  replaced: number
  /** `block.name` of each replaced declaration, in import order. */
  replacedNames: string[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function importError(result: DataImportResult, message: string) {
  result.skipped += 1
  result.errors.push(message)
}

function noteReplacement(result: DataImportResult, blockName: string, name: string) {
  result.replaced += 1
  result.replacedNames.push(`${blockName}.${name}`)
}

function isRecordAt(value: unknown, key: string): boolean {
  return isRecord(value) && Object.prototype.hasOwnProperty.call(value, key)
}

export function applyImportedCategory(category: DataCategory, rows: unknown[]): DataImportResult {
  const result: DataImportResult = {
    imported: 0,
    skipped: 0,
    errors: [],
    replaced: 0,
    replacedNames: [],
  }
  const store = useStudioStore.getState()
  const blocks = store.currentProject?.blocks
  if (!blocks) {
    return {
      imported: 0,
      skipped: rows.length,
      errors: ['Open a project before importing data'],
      replaced: 0,
      replacedNames: [],
    }
  }

  for (const [index, row] of rows.entries()) {
    if (!isRecord(row)) {
      importError(result, `${category}[${index}] must be an object`)
      continue
    }

    if (category === 'variables') {
      const blockName = readNonEmptyString(row.blockName)
      const name = readNonEmptyString(row.name)
      const scope = readNonEmptyString(row.scope) ?? 'state'
      if (!blockName || !blocks[blockName] || !name) {
        importError(result, `variables[${index}] has an unknown block or missing name`)
        continue
      }
      const type = readNonEmptyString(row.valueType) ?? 'string'
      const existingScopeMap = (blocks[blockName]?.state as Record<string, unknown> | undefined)?.[
        scope === 'state' ? 'state' : scope
      ]
      if (isRecordAt(existingScopeMap, name)) noteReplacement(result, blockName, name)
      store.addVariable(blockName, scope, name, {
        type,
        ...(Object.prototype.hasOwnProperty.call(row, 'defaultValue') ? { default: row.defaultValue } : {}),
      })
      const extras: Record<string, unknown> = {}
      if (typeof row.persist === 'boolean') extras.persist = row.persist
      if (typeof row.expression === 'string') extras.expression = row.expression
      if (Object.keys(extras).length > 0) {
        store.updateVariable(blockName, scope, name, extras)
      }
      result.imported += 1
      continue
    }

    if (category === 'datasources') {
      const blockName = readNonEmptyString(row.blockName)
      const name = readNonEmptyString(row.name)
      if (blockName === 'app') {
        importError(result, `datasources[${index}] is app-scoped and read-only in Data`)
        continue
      }
      if (!blockName || !blocks[blockName] || !name) {
        importError(result, `datasources[${index}] has an unknown block or missing name`)
        continue
      }
      const config = isRecord(row.config) ? { ...row.config } : {}
      if (!('type' in config) && !('kind' in config)) {
        config.type = readNonEmptyString(row.sourceType) ?? 'rest'
      }
      if (isRecordAt(blocks[blockName]?.data, name)) noteReplacement(result, blockName, name)
      store.addDatasource(blockName, name, config)
      result.imported += 1
      continue
    }

    if (category === 'events') {
      const blockName = readNonEmptyString(row.blockName)
      const eventKey = readNonEmptyString(row.eventKey)
      if (!blockName || !blocks[blockName] || !eventKey) {
        importError(result, `events[${index}] has an unknown block or missing eventKey`)
        continue
      }
      if (eventKey in foldBundleEventBindings(blocks[blockName]?.events)) {
        noteReplacement(result, blockName, eventKey)
      }
      store.addEventBinding(blockName, eventKey, {
        flow: readNonEmptyString(row.boundFlow) ?? undefined,
        params: isRecord(row.params) ? row.params : undefined,
      })
      result.imported += 1
      continue
    }

    importError(result, `flows[${index}] is an inventory row; import canonical ActionFlow JSON in Behavior`)
  }

  return result
}

function reportImport(result: DataImportResult, label: string) {
  if (result.imported > 0) {
    toast.success(
      `Imported ${result.imported} ${label}` +
        (result.replaced ? `; replaced ${result.replaced} existing` : '') +
        (result.skipped ? `; skipped ${result.skipped}` : ''),
    )
    // The replacement is the destructive half, so it gets its own line and
    // names a casualty — a count alone does not tell you what to go look at.
    if (result.replaced > 0) {
      const shown = result.replacedNames.slice(0, 3).join(', ')
      const more = result.replacedNames.length - 3
      toast.warning(
        `Overwrote ${result.replaced} existing declaration${result.replaced === 1 ? '' : 's'}: ` +
          `${shown}${more > 0 ? ` and ${more} more` : ''}`,
      )
    }
    if (result.skipped > 0 && result.errors[0]) toast.warning(result.errors[0])
    return
  }
  toast.warning(result.errors[0] ?? `No ${label} imported`)
}

export function exportAllData(index: EntityIndex, projectName: string) {
  const bundle: ProjectBundle = {
    version: 1,
    projectName,
    exportedAt: new Date().toISOString(),
    data: {
      variables: index.variables,
      datasources: index.datasources,
      events: index.events,
      flows: index.flows,
    },
  }
  downloadJson(bundle, 'cogo-project-data.json')
  toast.success('Exported project data bundle')
}

// ─── Import ─────────────────────────────────────────────────────

function pickJsonFile(): Promise<string | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) {
        resolve(null)
        return
      }
      resolve(await file.text())
    }
    input.click()
  })
}

export async function importCategory(category: DataCategory) {
  const text = await pickJsonFile()
  if (!text) return

  try {
    const data = JSON.parse(text)
    if (!Array.isArray(data)) {
      toast.error(`Invalid format: expected an array of ${category}`)
      return
    }
    const result = applyImportedCategory(category, data)
    reportImport(result, category)
  } catch {
    toast.error(`Failed to parse ${category} file`)
  }
}

export async function importAllData() {
  const text = await pickJsonFile()
  if (!text) return

  try {
    const bundle = JSON.parse(text) as ProjectBundle
    if (bundle.version !== 1 || !bundle.data) {
      toast.error('Invalid project bundle format')
      return
    }
    const categories: DataCategory[] = ['variables', 'datasources', 'events', 'flows']
    const result = categories.reduce<DataImportResult>(
      (summary, category) => {
        const rows = Array.isArray(bundle.data[category]) ? bundle.data[category] : []
        const next = applyImportedCategory(category, rows)
        summary.imported += next.imported
        summary.skipped += next.skipped
        summary.errors.push(...next.errors)
        // A bundle import is where the overwrite matters most — four
        // categories at once, over a project the user already has.
        summary.replaced += next.replaced
        summary.replacedNames.push(...next.replacedNames)
        return summary
      },
      { imported: 0, skipped: 0, errors: [], replaced: 0, replacedNames: [] },
    )
    reportImport(result, `entities from "${bundle.projectName}"`)
  } catch {
    toast.error('Failed to parse project bundle')
  }
}
